import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import {
  Button, Input, Select, FormField, Dialog,
  Badge, Spinner, Empty, PageHeader,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui";
import { Plus, Users } from "lucide-react";
import { toast } from "sonner";

function emptyPersonal() {
  return {
    nombre: "", ci: "", cargo: "",
    tipo_salario: "MENSUAL", salario_base: 0,
    telefono: "", email: "",
    es_tecnico: false, especialidad: "AMBOS",
  };
}

const ESPECIALIDAD_LABEL = { CHAPA: "Chapa", PINTURA: "Pintura", AMBOS: "Ambos" };

export default function Tecnicos() {
  const [list,   setList]   = useState([]);
  const [load,   setLoading] = useState(true);
  const [open,   setOpen]   = useState(false);
  const [edit,   setEdit]   = useState(null);
  const [form,   setForm]   = useState(emptyPersonal());
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    const r = await api.get("/personal");
    setList(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openNew() {
    setEdit(null);
    setForm(emptyPersonal());
    setOpen(true);
  }

  function openEdit(p) {
    setEdit(p);
    setForm({
      nombre:       p.nombre,
      ci:           p.ci ?? "",
      cargo:        p.cargo ?? "",
      tipo_salario: p.tipo_salario,
      salario_base: p.salario_base,
      telefono:     p.telefono ?? "",
      email:        p.email ?? "",
      es_tecnico:   p.es_tecnico,
      especialidad: p.especialidad ?? "AMBOS",
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = {
      nombre:       form.nombre,
      ci:           form.ci || null,
      cargo:        form.cargo || null,
      tipo_salario: form.tipo_salario,
      salario_base: form.salario_base,
      telefono:     form.telefono || null,
      email:        form.email || null,
      es_tecnico:   form.es_tecnico,
      especialidad: form.es_tecnico ? form.especialidad : null,
    };
    try {
      if (edit) {
        await api.put(`/personal/${edit.id}`, body);
        toast.success("Personal actualizado");
      } else {
        await api.post("/personal", body);
        toast.success("Personal agregado");
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActivo(p) {
    await api.put(`/personal/${p.id}`, { activo: !p.activo });
    reload();
  }

  const activos   = list.filter((p) => p.activo);
  const inactivos = list.filter((p) => !p.activo);

  if (load) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Personal" sub={`${activos.length} activos${inactivos.length ? ` · ${inactivos.length} inactivos` : ""}`}>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4" /> Nuevo
        </Button>
      </PageHeader>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>CI</TableHead>
            <TableHead>Cargo</TableHead>
            <TableHead>Rol / Especialidad</TableHead>
            <TableHead>Tipo salario</TableHead>
            <TableHead className="text-right">Salario base</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Empty message="Sin personal registrado" icon={Users} />
              </TableCell>
            </TableRow>
          )}
          {list.map((p) => (
            <TableRow key={p.id} className={!p.activo ? "opacity-50" : ""}>
              <TableCell className="font-medium text-sm">{p.nombre}</TableCell>
              <TableCell className="font-mono text-xs">{p.ci ?? "—"}</TableCell>
              <TableCell className="text-sm">{p.cargo ?? "—"}</TableCell>
              <TableCell>
                {p.es_tecnico ? (
                  <div className="flex items-center gap-1.5">
                    <Badge variant="success">Técnico</Badge>
                    {p.especialidad && (
                      <span className="text-xs text-muted-foreground">
                        {ESPECIALIDAD_LABEL[p.especialidad] ?? p.especialidad}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Funcionario</span>
                )}
              </TableCell>
              <TableCell className="text-sm">{p.tipo_salario}</TableCell>
              <TableCell className="text-right font-mono text-sm">
                {Number(p.salario_base).toLocaleString("es-PY")}
              </TableCell>
              <TableCell className="text-sm">{p.telefono ?? "—"}</TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <button onClick={() => openEdit(p)} className="text-xs text-primary hover:underline">
                    Editar
                  </button>
                  <button onClick={() => toggleActivo(p)} className="text-xs text-muted-foreground hover:underline">
                    {p.activo ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} title={edit ? "Editar personal" : "Nuevo personal"} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
            </FormField>
            <FormField label="CI">
              <Input value={form.ci} onChange={(e) => s("ci", e.target.value)} />
            </FormField>
            <FormField label="Cargo">
              <Input value={form.cargo} onChange={(e) => s("cargo", e.target.value)} />
            </FormField>
            <FormField label="Tipo salario">
              <Select value={form.tipo_salario} onChange={(e) => s("tipo_salario", e.target.value)}>
                <option value="MENSUAL">Mensual</option>
                <option value="QUINCENAL">Quincenal</option>
                <option value="POR_TRABAJO">Por trabajo</option>
              </Select>
            </FormField>
            <FormField label="Salario base">
              <Input
                type="number" min="0"
                value={form.salario_base}
                onChange={(e) => s("salario_base", parseFloat(e.target.value) || 0)}
              />
            </FormField>
            <FormField label="Teléfono">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
            <FormField label="Email">
              <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
            </FormField>
          </div>

          {/* Toggle técnico */}
          <div className="border rounded-lg p-3 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.es_tecnico}
                onChange={(e) => s("es_tecnico", e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium">Es técnico de taller</span>
            </label>
            {form.es_tecnico && (
              <FormField label="Especialidad">
                <Select value={form.especialidad} onChange={(e) => s("especialidad", e.target.value)}>
                  <option value="CHAPA">Chapa</option>
                  <option value="PINTURA">Pintura</option>
                  <option value="AMBOS">Ambos (chapa y pintura)</option>
                </Select>
              </FormField>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
